import type { LegalDocument } from '@/schemas/schema';
import { SIG_PAGE_LAYOUT, getSignatureBlockPosition } from '@/lib/pdf/signature-layout';
import { updateContractDocumentSigDocumentId } from '@/modules/org/contracts/services/contract-document.service';
import { createContractDocumentEvent } from '@/modules/org/contracts/services/contract-document-event.service';
import { createContractSignatureLog } from '@/modules/org/contracts/services/signature-log.service';
import { useAuthStore } from '@/state/store/auth';
import { bodyErrorMessage, isSetupAlreadyDone, responseErrorMessage } from '@/lib/utils/api-error';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SignatureField {
  id: string;
  type: 'signature' | 'text' | 'date';
  signatoryIndex: number;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
}

interface SignatoryInput {
  name: string;
  email: string;
  role?: string;
  order?: number;
  phone?: string;
}

export interface RolloutPayload {
  draftId: string;
  pdfBase64: string;
  document: LegalDocument;
  formData: Record<string, unknown>;
  signatories: SignatoryInput[];
  signatureFields: SignatureField[];
  templateSlug: string;
  templateTitle: string;
  numPages: number;
}

export interface RolloutResult {
  success: boolean;
  documentId: string;
  trackingId: string;
  fileId: string;
  events: unknown;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_API_URL ?? '';

const STORAGE_API = `${BACKEND_BASE_URL}/signature/upload-url`;
const PREPARE_API = `${BACKEND_BASE_URL}/signature/prepare-contract`;
const ROLLOUT_API = `${BACKEND_BASE_URL}/signature/rollout-contract`;
const GET_EVENTS_API = `${BACKEND_BASE_URL}/signature/events`;
const GET_ORGANIZATION_API = `${BACKEND_BASE_URL}/signature/get-organizations`;
const REGISTER_ORGANIZATION_API = `${BACKEND_BASE_URL}/signature/register-organization`;
const REGISTER_ORGANIZATION_WEBHOOK_API = `${BACKEND_BASE_URL}/signature/register-organization-webhook`;

// Default SELISE-formatted timezone used when the caller doesn't supply one.
// The signature API requires a SELISE-formatted string (e.g. "(GMT+06:00) Dhaka"),
// not an IANA name — wire a picker through `timezone` to let users choose.
const DEFAULT_SIGNATURE_ORG_TIMEZONE = '(GMT+06:00) Dhaka';
const SIG_ORG_POLL_TIMEOUT_MS = 120_000;
const SIG_ORG_POLL_INTERVAL_MS = 2_000;

const CLIENT_TO_STAMP_POST_INFO_SCALE = 72 / 96;
const DATE_FIELD_X_OFFSET = 8;
const DATE_FIELD_Y_OFFSET = 4;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function uuid(): string {
  return crypto.randomUUID();
}

function mapDateFieldToStampPostInfoUnits(
  field: Pick<SignatureField, 'x' | 'y' | 'width' | 'height'>
) {
  const to = (v: number) => Math.round(v * CLIENT_TO_STAMP_POST_INFO_SCALE);
  return {
    x: Math.max(0, to(field.x - DATE_FIELD_X_OFFSET)),
    y: Math.max(0, to(field.y - DATE_FIELD_Y_OFFSET)),
    width: Math.max(1, to(field.width)),
    height: Math.max(1, to(field.height)),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Headers for the backend signature proxy calls (upload-url, prepare-contract,
// rollout-contract, events). When a user is signed in, forward their Blocks
// access token and active org id so the backend can scope the request to that
// org. Guests fall through with just the JSON content-type (unchanged behavior).
function signatureProxyHeaders(): Record<string, string> {
  const { orgId, sigOrgId } = useAuthStore.getState();
  return {
    'Content-Type': 'application/json',
    ...(orgId ? { 'x-blocks-org-id': orgId } : {}),
    ...(sigOrgId ? { 'x-sig-org-id': sigOrgId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Backend proxy calls
//
// All SELISE traffic now goes through the Node backend at VITE_BACKEND_API_URL.
// The backend handles OAuth token caching, so the browser never sees the
// SELISE client_id/client_secret. Each proxy response is wrapped as
// `{ success, statusCode, message, data }` — we unwrap `.data` to get the
// original SELISE upstream payload.
// ---------------------------------------------------------------------------

async function uploadPdfToStorage(
  pdfBase64: string,
  document: LegalDocument,
  templateSlug?: string
): Promise<{ fileId: string; fileName: string }> {
  const fileId = uuid();

  const firstSignatoryName = document?.signatories?.[0]?.name || 'Document';
  const sanitizedName = String(firstSignatoryName)
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');

  const documentType =
    document?.metadata?.title?.replace(/\s+/g, '_') ||
    templateSlug?.replace(/-/g, '_') ||
    'Legal_Document';
  const fileName = `${documentType}_${sanitizedName}_${Date.now()}.pdf`;
  const tag = templateSlug ? templateSlug.replace(/-/g, '') : 'LegalDocument';

  const preSignResponse = await fetch(STORAGE_API, {
    method: 'POST',
    credentials: 'include',
    headers: signatureProxyHeaders(),
    body: JSON.stringify({
      ItemId: fileId,
      MetaData: '{}',
      Name: fileName,
      ParentDirectoryId: '',
      Tags: `["File","${tag}"]`,
      AccessModifier: 'Private',
    }),
  });

  if (!preSignResponse.ok) {
    const text = await preSignResponse.text();
    throw new Error(`Failed to get pre-signed URL: ${preSignResponse.status} ${text}`);
  }

  const wrapped = await preSignResponse.json();
  const { UploadUrl, FileId } = wrapped?.data ?? {};

  // Decode base64 to Uint8Array for upload
  const binaryString = atob(pdfBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const uploadResponse = await fetch(UploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'application/pdf',
      'Content-Length': bytes.length.toString(),
    },
    body: bytes,
  });

  if (!uploadResponse.ok) {
    const text = await uploadResponse.text();
    throw new Error(`Failed to upload PDF blob: ${uploadResponse.status} ${text}`);
  }

  return { fileId: FileId as string, fileName };
}

// ---------------------------------------------------------------------------
// Prepare payload builder
// ---------------------------------------------------------------------------

function buildPreparePayload({
  fileId,
  document,
  signatories,
  signatureFields,
  templateSlug,
  templateTitle,
  numPages,
}: {
  fileId: string;
  document: LegalDocument;
  signatories: SignatoryInput[];
  signatureFields?: SignatureField[];
  templateSlug?: string;
  templateTitle?: string;
  numPages?: number;
}) {
  const trackingId = uuid();

  const documentTitle = document?.metadata?.title || templateTitle || 'Legal Document';
  const firstSignatoryName = signatories[0]?.name || 'Document';
  const title = `${documentTitle} - ${firstSignatoryName}`;

  const sortedSignatories = [...signatories].sort((a, b) => {
    const orderA = typeof a.order === 'number' && a.order > 0 ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b.order === 'number' && b.order > 0 ? b.order : Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  const signatoryMeta = sortedSignatories.map((s, i) => {
    const fallbackOrder = i + 1;
    const resolvedOrder = typeof s.order === 'number' && s.order > 0 ? s.order : fallbackOrder;
    return { ...s, resolvedOrder, name: s.name || `Signatory ${fallbackOrder}` };
  });

  const ownerEmail = signatoryMeta[0]?.email;
  if (!ownerEmail) throw new Error('Unable to determine owner email from signatories.');

  const addSignatoryCommands = signatoryMeta.map((s) => {
    const parts = (s.name?.trim() || 'Signatory').split(/\s+/);
    return {
      Email: s.email,
      ContractRole: 0,
      FirstName: parts[0] || 'Signatory',
      LastName: parts.length > 1 ? parts.slice(1).join(' ') : 'Participant',
      Phone: s.phone || '',
    };
  });

  const signingOrders = signatoryMeta.map((s) => ({
    Email: s.email,
    Order: s.resolvedOrder,
  }));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stampCoordinates: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const textFieldCoordinates: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stampPostInfoCoordinates: any[] = [];

  // Map client overlay fields to SELISE coordinate format
  if (Array.isArray(signatureFields) && signatureFields.length > 0) {
    for (const field of signatureFields) {
      const signatory = signatoryMeta[field.signatoryIndex];
      if (!signatory) continue;

      const pageNumber = Math.max(0, (field.pageNumber || 1) - 1);
      const providedId =
        typeof field.id === 'string' && /^[0-9a-f-]{36}$/i.test(field.id) ? field.id : uuid();

      const common = {
        FileId: fileId,
        PageNumber: pageNumber,
        Width: field.width,
        Height: field.height,
        X: field.x,
        Y: field.y,
        SignatoryEmail: signatory.email,
        CoordinateId: providedId,
        SignatoryGroupId: null,
        Order: signatory.resolvedOrder,
        SignatoryId: null,
        SignatoryName: signatory.name || 'Signatory',
      };

      if (field.type === 'signature') {
        stampCoordinates.push({ ...common, SignatureImageFileId: null });
      } else if (field.type === 'date') {
        const adj = mapDateFieldToStampPostInfoUnits(field);
        stampPostInfoCoordinates.push({
          ...common,
          Width: adj.width,
          Height: adj.height,
          X: adj.x,
          Y: adj.y,
          EntityName: 'AuditLog',
          PropertyName: '{StampTime}',
          FontDetails: { FontName: 'Arial', FontSize: 12 },
        });
      } else if (field.type === 'text') {
        textFieldCoordinates.push({ ...common, Value: signatory.name });
      }
    }
  }

  // Fallback: default positions if no fields provided
  if (stampCoordinates.length === 0 && signatoryMeta.length > 0) {
    const DPI_SCALE = 96 / 72;
    const MARGIN_LEFT = SIG_PAGE_LAYOUT.MARGIN_X;
    const lastPageIndex = typeof numPages === 'number' && numPages > 0 ? numPages - 1 : 0;

    signatoryMeta.forEach((signatory) => {
      const blockTop = getSignatureBlockPosition(signatoryMeta.indexOf(signatory));

      const sigX = Math.round((MARGIN_LEFT + SIG_PAGE_LAYOUT.SIG_BOX_X_OFFSET) * DPI_SCALE);
      const sigY = Math.round((blockTop + SIG_PAGE_LAYOUT.SIG_BOX_Y_OFFSET) * DPI_SCALE);
      const sigWidth = Math.round(SIG_PAGE_LAYOUT.SIG_BOX_WIDTH * DPI_SCALE);
      const sigHeight = Math.round(SIG_PAGE_LAYOUT.SIG_BOX_HEIGHT * DPI_SCALE);

      const dateX = Math.round((MARGIN_LEFT + SIG_PAGE_LAYOUT.DATE_BOX_X_OFFSET) * DPI_SCALE);
      const dateY = Math.round((blockTop + SIG_PAGE_LAYOUT.SIG_BOX_Y_OFFSET) * DPI_SCALE);
      const dateWidth = Math.round(SIG_PAGE_LAYOUT.DATE_BOX_WIDTH * DPI_SCALE);
      const dateHeight = Math.round(SIG_PAGE_LAYOUT.SIG_BOX_HEIGHT * DPI_SCALE);
      const datePos = mapDateFieldToStampPostInfoUnits({
        x: dateX,
        y: dateY,
        width: dateWidth,
        height: dateHeight,
      });

      stampCoordinates.push({
        FileId: fileId,
        Width: sigWidth,
        Height: sigHeight,
        PageNumber: lastPageIndex,
        X: sigX,
        Y: sigY,
        SignatoryEmail: signatory.email,
        SignatureImageFileId: null,
        CoordinateId: uuid(),
        SignatoryGroupId: null,
        Order: signatory.resolvedOrder,
        SignatoryId: null,
        SignatoryName: signatory.name || 'Signatory',
      });

      stampPostInfoCoordinates.push({
        FileId: fileId,
        Width: datePos.width,
        Height: datePos.height,
        PageNumber: lastPageIndex,
        X: datePos.x,
        Y: datePos.y,
        EntityName: 'AuditLog',
        PropertyName: '{StampTime}',
        SignatoryEmail: signatory.email,
        CoordinateId: uuid(),
        SignatoryGroupId: null,
        Order: signatory.resolvedOrder,
        SignatoryId: null,
        FontDetails: { FontName: 'Arial', FontSize: 12 },
      });
    });
  }

  const redirectPath = templateSlug || document?.metadata?.documentType || 'templates';

  const prepareCommand = {
    TrackingId: trackingId,
    Title: title,
    ContractType: 0,
    ReturnDocument: true,
    ReceiveRolloutEmail: true,
    SignatureClass: 0,
    Language: 'en-US',
    LandingPageType: 0,
    ReminderPulse: 168,
    OwnerEmail: ownerEmail,
    FileIds: [fileId],
    AddSignatoryCommands: addSignatoryCommands,
    SigningOrders: signingOrders,
    RedirectUrl: `${window.location.origin}/templates/${redirectPath}`,
  };

  return { prepareCommand, stampCoordinates, textFieldCoordinates, stampPostInfoCoordinates };
}

// ---------------------------------------------------------------------------
// Prepare & Rollout steps
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function prepareContract(prepareCommand: any): Promise<string> {
  const response = await fetch(PREPARE_API, {
    method: 'POST',
    credentials: 'include',
    headers: signatureProxyHeaders(),
    body: JSON.stringify(prepareCommand),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`PrepareContract failed: ${response.status} ${text}`);
  }

  const wrapped = await response.json();
  const result = wrapped?.data ?? {};
  const documentId = result.Result?.DocumentId || result.DocumentId;

  if (!documentId) {
    throw new Error('No DocumentId in PrepareContract response');
  }

  return documentId;
}

async function waitForEvent(
  documentId: string,
  status: string,
  maxAttempts: number,
  delayMs: number
): Promise<boolean> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const response = await fetch(GET_EVENTS_API, {
      method: 'POST',
      credentials: 'include',
      headers: signatureProxyHeaders(),
      body: JSON.stringify({
        DocumentId: documentId,
        Type: 'DocumentStatus',
        Status: status,
      }),
    });

    if (response.ok) {
      const wrapped = await response.json();
      const events = wrapped?.data;
      if (Array.isArray(events) && events.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const found = events.find((e: any) => e.Status === status && e.Success === true);
        if (found) return true;
      }
    }

    if (attempt < maxAttempts) await sleep(delayMs);
  }

  return false;
}

async function rolloutContract(
  documentId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coordinates: {
    stampCoordinates: any[];
    textFieldCoordinates: any[];
    stampPostInfoCoordinates: any[];
  }
): Promise<void> {
  const response = await fetch(ROLLOUT_API, {
    method: 'POST',
    credentials: 'include',
    headers: signatureProxyHeaders(),
    body: JSON.stringify({
      DocumentId: documentId,
      StampCoordinates: coordinates.stampCoordinates,
      TextFieldCoordinates: coordinates.textFieldCoordinates,
      StampPostInfoCoordinates: coordinates.stampPostInfoCoordinates,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`RolloutContract failed: ${response.status} ${text}`);
  }
}

async function waitForRolloutSuccess(documentId: string): Promise<unknown> {
  const maxAttempts = 10;
  const delayMs = 5000;
  let lastEvents: unknown = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const response = await fetch(GET_EVENTS_API, {
      method: 'POST',
      credentials: 'include',
      headers: signatureProxyHeaders(),
      body: JSON.stringify({ DocumentId: documentId }),
    });

    if (!response.ok) {
      if (attempt < maxAttempts) {
        await sleep(delayMs);
        continue;
      }
      return { error: `GetEvents failed: ${response.status}` };
    }

    const wrapped = await response.json();
    const events = wrapped?.data;
    lastEvents = events;

    if (Array.isArray(events)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasRolloutEvent = events.some(
        (e: any) => e.Status === 'rollout_success' || e.Status === 'rollout_failed'
      );
      if (hasRolloutEvent) return events;
    }

    if (attempt < maxAttempts) await sleep(delayMs);
  }

  return lastEvents;
}

// ---------------------------------------------------------------------------
// Main export — full two-step signature workflow
// ---------------------------------------------------------------------------

/**
 * Client-side SELISE signature rollout workflow.
 * Replaces old Next.js `/api/signature/rollout` route.
 */
export async function signatureRollout(payload: RolloutPayload): Promise<RolloutResult> {
  const {
    pdfBase64,
    document,
    signatories,
    signatureFields,
    templateSlug,
    templateTitle,
    numPages,
    draftId,
  } = payload;

  // 1. Upload PDF (backend proxy obtains the SELISE token internally)
  const { fileId } = await uploadPdfToStorage(pdfBase64, document, templateSlug);

  // 2. Build prepare payload
  const { prepareCommand, stampCoordinates, textFieldCoordinates, stampPostInfoCoordinates } =
    buildPreparePayload({
      fileId,
      document,
      signatories,
      signatureFields,
      templateSlug,
      templateTitle,
      numPages,
    });

  // 3. Prepare contract
  const documentId = await prepareContract(prepareCommand);

  // 1) update SigDocumentId on the contract document
  // 2) record a rollout_success event row attributed to the user who clicked
  //    Send for Signature. The SELISE webhook is configured to NOT emit a
  //    rollout_success event — the frontend owns this one with actor info.
  const { userId, user, orgUDSId } = useAuthStore.getState();
  const initiatorName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';
  const initiatorPhoto = user?.profileImageUrl ?? '';
  const rolloutOccurredAt = new Date().toISOString();

  await updateContractDocumentSigDocumentId(draftId, documentId);

  try {
    await createContractDocumentEvent({
      ContractDocumentId: draftId,
      OrganizationId: orgUDSId ?? '',
      OrganizationIds: orgUDSId ? [orgUDSId] : undefined,
      SigDocumentId: documentId,
      EventType: 'rollout_success',
      OccurredAt: rolloutOccurredAt,
      ActorUserId: userId ?? '',
      ActorUserName: initiatorName,
      ActorUserPhoto: initiatorPhoto,
    });
  } catch (eventErr) {
    console.error('Failed to record rollout_success event:', eventErr);
  }

  // Persist a signature log row capturing the prepare snapshot + initiator.
  // Best-effort: if this fails we still let the SELISE rollout continue.
  try {
    await createContractSignatureLog({
      ContractDocumentId: draftId,
      OrganizationId: orgUDSId ?? '',
      InitiatedByUserId: userId ?? '',
      SigDocumentId: documentId,
      TrackingId: prepareCommand.TrackingId,
      Payload: JSON.stringify({
        prepareCommand,
        stampCoordinates,
        textFieldCoordinates,
        stampPostInfoCoordinates,
        fileId,
        numPages,
      }),
      Status: 'generation_completed',
    });
  } catch (logError) {
    console.error('Failed to save contract signature log:', logError);
  }

  // 4. Wait for preparation_success
  const prepared = await waitForEvent(documentId, 'preparation_success', 20, 2000);
  if (!prepared) {
    throw new Error('Contract preparation failed or timed out');
  }

  // 5. Rollout with coordinates
  await rolloutContract(documentId, {
    stampCoordinates,
    textFieldCoordinates,
    stampPostInfoCoordinates,
  });

  // 6. Wait for rollout_success
  const events = await waitForRolloutSuccess(documentId);

  if (Array.isArray(events)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rolloutEvent = events.find(
      (e: any) => e.Status === 'rollout_success' || e.Status === 'rollout_failed'
    );
    if (rolloutEvent?.Status === 'rollout_failed') {
      throw new Error('Contract was prepared but rollout failed. Check SELISE portal.');
    }
  }

  return {
    success: true,
    documentId,
    trackingId: prepareCommand.TrackingId,
    fileId,
    events,
  };
}

export async function getSignatureOrganizations(): Promise<void> {
  const response = await fetch(GET_ORGANIZATION_API, {
    method: 'GET',
    credentials: 'include',
    headers: signatureProxyHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`RolloutContract failed: ${response.status} ${text}`);
  }

  return await response.json();
}

// ---------------------------------------------------------------------------
// Signature (ARC) organization registration
// ---------------------------------------------------------------------------

export interface SignatureOrganization {
  OrganizationId: string;
  OrganizationName: string;
  Role: string;
  CreatedDate: string;
  IsDefaultOrganization: boolean;
}

export interface RegisterSignatureOrganizationPayload {
  firstName: string;
  lastName: string;
  email: string; // MUST match the Bearer token's email claim
  name: string;
  timezone?: string; // SELISE-formatted, e.g. "(GMT+06:00) Dhaka"
}

async function fetchSignatureOrganizationList(): Promise<SignatureOrganization[]> {
  const response = await fetch(GET_ORGANIZATION_API, {
    method: 'GET',
    credentials: 'include',
    headers: signatureProxyHeaders(),
  });

  if (!response.ok) {
    throw new Error(await responseErrorMessage(response));
  }

  const wrapped = await response.json();
  return wrapped?.data?.organizations ?? [];
}

// Pick the signature org to reuse when ARC reports the account is already set
// up. Prefer an exact name match (so a same-named re-create merges cleanly),
// then the account's default org, then the only/first org available.
function pickExistingSignatureOrg(
  orgs: SignatureOrganization[],
  name: string
): SignatureOrganization | undefined {
  return (
    orgs.find((o) => o.OrganizationName === name) ??
    orgs.find((o) => o.IsDefaultOrganization) ??
    orgs[0]
  );
}

/**
 * Registers a new organization in the SELISE signature (ARC) system and returns
 * its OrganizationId.
 *
 * Happy path (201): the backend already waited server-side for SELISE and the
 * new org is returned inline — we use it directly. Slow-SELISE path (202): the
 * backend gives up waiting and returns `data: null`, so we fall back to polling
 * `/get-organizations` until an org with a matching name surfaces.
 *
 * Already-set-up path: ARC lets a user sign up only once, so a repeat signup
 * returns "Setup already done." Rather than error out, we resolve the user's
 * existing signature org and return it, making this function idempotent so the
 * caller's pipeline flows straight into a silent merge/connect.
 */
export async function registerSignatureOrganization(
  payload: RegisterSignatureOrganizationPayload
): Promise<string> {
  const body = {
    User: {
      FirstName: payload.firstName,
      LastName: payload.lastName,
      Email: payload.email,
    },
    Organization: {
      Name: payload.name,
      Timezone: payload.timezone ?? DEFAULT_SIGNATURE_ORG_TIMEZONE,
    },
  };

  const response = await fetch(REGISTER_ORGANIZATION_API, {
    method: 'POST',
    credentials: 'include',
    headers: signatureProxyHeaders(),
    body: JSON.stringify(body),
  });

  const text = await response.text();

  if (isSetupAlreadyDone(text)) {
    const orgs = await fetchSignatureOrganizationList();
    const existing = pickExistingSignatureOrg(orgs, payload.name);
    if (existing?.OrganizationId) return existing.OrganizationId;
  }

  if (!response.ok) {
    // ARC allows a user to sign up (create their initial org) only once. A
    // repeat signup returns "Setup already done." — this is not a failure for
    // us: the user already has a signature org, so we transparently resolve it
    // and let the org-provisioning pipeline continue (a silent merge/connect),
    // exactly as `connectSignatureOrg` would, without surfacing any error.

    throw new Error(bodyErrorMessage(text, response.status));
  }

  let wrapped: any = {};
  try {
    if (text) wrapped = JSON.parse(text);
  } catch {
    // ignore parse errors
  }

  // 201 — new org returned inline, no polling needed.
  const inlineId: string | undefined = wrapped?.data?.organization?.OrganizationId;
  if (inlineId) return inlineId;

  // 202 — SELISE was slow; poll until the org with our name appears.
  const startedAt = Date.now();
  while (Date.now() - startedAt < SIG_ORG_POLL_TIMEOUT_MS) {
    await sleep(SIG_ORG_POLL_INTERVAL_MS);
    const orgs = await fetchSignatureOrganizationList();
    const match = orgs.find((o) => o.OrganizationName === payload.name);
    if (match?.OrganizationId) return match.OrganizationId;
  }

  throw new Error(
    `Signature organization "${payload.name}" was registered but did not appear within ${SIG_ORG_POLL_TIMEOUT_MS / 1000
    }s`
  );
}

export interface RegisterWebhookResult {
  created: boolean;
  organizationId: string;
  webhookUrl: string;
  itemId: string;
}

/**
 * Registers (or confirms) the signature webhook for an organization so the
 * backend starts receiving e-signature events (preparation, rollout, signing,
 * completion). Idempotent — re-calling just returns the existing registration
 * (`created: false`), so it's safe to fire after every org create/connect.
 *
 * @param organizationId The SELISE signature OrganizationId (i.e. the SigOrgId)
 *                       the caller administers.
 * @param email          Must equal the Bearer token's email claim.
 */
export async function registerOrganizationWebhook(
  organizationId: string,
  email: string
): Promise<RegisterWebhookResult> {
  const response = await fetch(REGISTER_ORGANIZATION_WEBHOOK_API, {
    method: 'POST',
    credentials: 'include',
    headers: signatureProxyHeaders(),
    body: JSON.stringify({ organizationId, email }),
  });

  if (!response.ok) {
    throw new Error(await responseErrorMessage(response));
  }

  const wrapped = await response.json();
  return wrapped?.data;
}
